import type { CommonAPITeamIdFilter, CommonTeamIdFilter } from '../permissions/filterHelpers';

import type { DependencyFilters } from './filterSchema';
import type { HydrationOptions } from './hydrationOptions';

export type DependencyPagination = {
  page?: number;
  count?: number;
}

export enum DepthFilterOptions {
  Direct = 'direct',
  Transitive = 'transitive',
}

export enum LayerDepthFilterOptions {
  Base = 'base',
  Other = 'other',
}

export enum HasIssuesFilterOptions {
  HasIssues = 'hasIssues',
  HasLicensingIssues = 'hasLicensingIssues',
  HasQualityIssues = 'hasQualityIssues',
  HasVulnIssues = 'hasVulnIssues',
  NoIssues = 'noIssues',
}

export enum DependencyFilterKeys {
  /**
   * Filter by *dependency* locator. Behaviour varies by data source:
   * `DependencyLocks` performs a case-insensitive substring match (`LIKE %locator%`)
   * to support partial-locator search from the frontend; other sources perform an
   * exact match. Use {@link DependencyFilterKeys.ExactLocators} when callers need a
   * case-sensitive, index-friendly `IN`-style match in every data source.
   */
  Locators = 'locators',
  /**
   * Filter by exact *dependency* locator match (case-sensitive `IN`-style match in
   * every data source). Prefer this over {@link DependencyFilterKeys.Locators} for
   * backend lookups that need exact match semantics — e.g. looking up depth/metadata
   * by known revision locators — since it remains index-friendly on `DependencyLocks`.
   */
  ExactLocators = 'exactLocators',
  Title = 'title',
  Status = 'status',
  Depth = 'depth',
  Confidence = 'confidence',
  LayerDepth = 'layerDepth',
  HasIssues = 'hasIssues',
  Licenses = 'licenses',
  Fetchers = 'fetchers',
  ShowIgnored = 'showIgnored',
  /**
   * Narrow the scope's root revisions. Values may be full revision locators ("pkg$rev") or project
   * locators ("pkg"); each root is kept if it matches either form.
   *
   * Accepting the project form matters for a global scope, which otherwise resolves every last-analyzed
   * revision in the organization and fetches dependencies for all of them: a caller that knows which
   * projects it needs can shrink that fan-out without first resolving each project's current revision.
   */
  RootProjects = 'rootProjects',
  PackageLabels = 'packageLabels',
  /**
   * Filter by locator prefix (since package labels might apply to entire projects).
   * This value is derived from 'packageLabels' and not used directly.
   */
  PackageLocators = 'packageLocators',
  /**
   * Filter by 'vendored', 'managed', or 'snippet'
   * Vendored is only supported on project scope, skipped otherwise
   */
  Sources = 'sources',
  /**
   * Filter by vendoredPath prefix - will filter to vendored depedencies found under this path
   * Only supported on project scope, skipped otherwise
   */
  VendoredPath = 'vendoredPath',
  /**
   * Exclude dependencies that match the given package label names.
   * Dependencies with these labels will be filtered OUT of the results.
   */
  ExcludePackageLabels = 'excludePackageLabels',
  /**
   * Filter by `Revision.resolved` status. When `true`, only dependencies FOSSA was able to analyze are returned;
   * when `false`, only unresolved dependencies (e.g. behind a private registry); when omitted, no filter is applied.
   */
  Resolved = 'resolved',
}

export type DependencyQueryWithFilters =
  DependencyFilters & DependencyPagination & HydrationOptions;

export enum ProjectFilterKeys {
  TeamIds = 'teamIds',
}
export type APIProjectFilters = {
  [ProjectFilterKeys.TeamIds]: CommonAPITeamIdFilter;
}

export type ProjectFilters = {
  [ProjectFilterKeys.TeamIds]?: CommonTeamIdFilter;
}
